// Client for the read-only HubSpot mirror API (/api/hubspot/*).
//
// DIRECTION IS HUBSPOT -> APP, ALWAYS. Every function below issues a GET. There
// is no create, no update, no note-push, no association write — not because we
// haven't written one yet, but because the feature is defined by its absence.
// If you are here to add a write, you are in the wrong repo: the human makes
// the change in HubSpot, and the mirror reflects it on the next sync. The
// `hubspot_url` on every record is what turns a suggestion into one click.
//
// Like gmail.js there is deliberately NO local-storage or sample-data fallback.
// A CRM mirror cannot be simulated: showing an invented deal would be worse
// than showing nothing, because a rep would act on it. When the API is
// unreachable, or HubSpot was never connected, these functions resolve with an
// honest shape the caller renders as "not connected" — they never invent rows.
//
// UNLIKE gmail.js, these functions do not throw. Every one of them feeds a
// render path on the lead detail page, and a rejected promise there is a blank
// page or a spinner that never ends. So each call catches its own failure and
// reports it in the return value: `available:false` means "we could not ask",
// which is a different fact from `configured:false` ("nobody has connected
// HubSpot") and from `matched:false` ("HubSpot is connected, this lead just
// isn't in it"). The UI says which.
import { getIdToken } from './cognito.js'

async function authHeaders(extra = {}) {
  const token = await getIdToken()
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// An HTTP-level rejection the server actually answered (as opposed to a
// transport failure, which surfaces as a plain Error). It carries the status and
// the server's own message so a caller can tell "you're locked out of this
// workspace" (403) apart from "we never reached the mirror". Without this the
// membership gate's 403 was flattened into the same "could not be reached" shape
// as a dropped connection, and a locked-out teammate saw a network excuse
// instead of the real reason.
class HubSpotHttpError extends Error {
  constructor(status, reason) {
    super(reason || `HubSpot API error (${status})`)
    this.name = 'HubSpotHttpError'
    this.status = status
    this.reason = reason || ''
  }
}

// Local dev has no /api proxy — the dev server's history fallback answers
// /api/hubspot/* with index.html — so, exactly like data.jsx and chat.js, the
// client falls through to the deployed API when the same-origin path is not
// really the API. Auth still applies (the local session's Cognito token rides
// along), so this changes WHERE the request goes, never WHO may ask.
const HUBSPOT_FALLBACK_BASE = 'https://hzpio22kcl.execute-api.us-east-1.amazonaws.com/prod'

// Every request in this module goes through here, and it only ever does a GET.
// One function to read if you want to convince yourself the client cannot
// write: there is no `method` argument and no body argument to pass one.
// `opts.signal` lets a caller abort an in-flight read when its page unmounts —
// aborting is still only a read that never happened, never a write.
async function get(action, params = {}, { signal } = {}) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    const v = String(value ?? '').trim()
    if (v) query.set(key, v)
  }
  const suffix = query.toString() ? `?${query}` : ''
  const headers = await authHeaders()
  let res = await fetch(`/api/hubspot/${action}${suffix}`, {
    method: 'GET',
    headers,
    ...(signal ? { signal } : {}),
  })
  let ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json') && HUBSPOT_FALLBACK_BASE) {
    res = await fetch(`${HUBSPOT_FALLBACK_BASE}/hubspot/${action}${suffix}`, {
      method: 'GET',
      headers,
      ...(signal ? { signal } : {}),
    })
    ct = res.headers.get('content-type') || ''
  }
  if (!ct.includes('application/json')) throw new Error('HubSpot API unavailable')
  const data = await res.json()
  // A JSON error the server chose to send (403 "not a member", 409, …) is a
  // real answer, not a transport failure — throw it typed so callers can react
  // to the status and quote the server's message.
  if (!res.ok) throw new HubSpotHttpError(res.status, data.error || '')
  return data
}

// The shape every failure resolves to. `available:false` is the only honest
// answer when we could not reach the API at all — we do not know whether
// HubSpot is connected, so we must not claim either way.
// The one POST in this module. Same base, same auth header and the same
// execute-api fallback as get() — but the fallback is tried only when the
// first answer was an error that never reached the lambda (a non-JSON 4xx/5xx
// off CloudFront or the gateway): a request with a side effect is never
// replayed after a 2xx, whatever shape the body came back in.
async function post(action, body = {}, { signal } = {}) {
  const headers = await authHeaders({ 'Content-Type': 'application/json' })
  const init = { method: 'POST', headers, body: JSON.stringify(body), ...(signal ? { signal } : {}) }
  let res = await fetch(`/api/hubspot/${action}`, init)
  let ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json') && !res.ok && HUBSPOT_FALLBACK_BASE) {
    res = await fetch(`${HUBSPOT_FALLBACK_BASE}/hubspot/${action}`, init)
    ct = res.headers.get('content-type') || ''
  }
  if (!ct.includes('application/json')) throw new Error('HubSpot API unavailable')
  const data = await res.json()
  if (!res.ok) throw new HubSpotHttpError(res.status, data.error || '')
  return data
}

function unreachable(extra = {}) {
  return {
    available: false,
    configured: false,
    connected: false,
    matched: false,
    reason: 'The HubSpot mirror could not be reached from this browser.',
    ...extra,
  }
}

// The server reached us and said no — the caller is signed in but not a member
// of this workspace (or otherwise forbidden). Distinct from `unreachable`: we
// DID get an answer, so we quote the server's own reason rather than blaming the
// network, and flag `forbidden` so a panel can render a locked-out state instead
// of a retry spinner. Nothing is confidently matched, so the confident-match
// fields stay false just like the unreachable shape.
function forbidden(reason, extra = {}) {
  return {
    available: false,
    configured: false,
    connected: false,
    matched: false,
    forbidden: true,
    reason: reason || 'You are not a member of this workspace.',
    ...extra,
  }
}

// Turn a caught failure into the right honest shape: a 403 the server answered
// becomes `forbidden` (surfacing its message); anything else — transport error,
// non-JSON, 5xx — stays `unreachable`. `extra` is the per-endpoint empty payload
// (the same fields each function already passes to unreachable).
function failShape(err, extra = {}) {
  if (err instanceof HubSpotHttpError && err.status === 403) {
    return forbidden(err.reason, extra)
  }
  return unreachable(extra)
}

// { available, configured, connected, last_sync_at, syncing, stale_pending,
//   last_error, objects: { company: {count, phase, backfilling}, ... } }
export async function hubspotStatus() {
  try {
    return { available: true, ...(await get('status')) }
  } catch (err) {
    return failShape(err)
  }
}

// Resolve a lead to a mirrored company. The server does exact key lookups only
// and reports which one won in `confidence`, plus EVERY company that shared
// that key in `candidates` — both are what let us refuse to call a coin-flip a
// match. When there is more than one the server itself answers matched:false,
// ambiguous:true; this client does not have to be the last line of defence,
// but it checks anyway (see matchVerdict).
// { available, configured, matched, ambiguous, company_id, confidence, company,
//   candidates: [{id, name, domain, hubspot_url}], candidate_count }
export async function hubspotMatch(lookup) {
  try {
    return { available: true, ...(await get('match', lookup)) }
  } catch (err) {
    return failShape(err, { company: null, candidates: [] })
  }
}

// Everything the mirror holds for one company: the company, its owner,
// contacts, deals, tasks and engagements, newest first.
// { available, configured, matched, company, owner, contacts[], deals[],
//   tasks[], engagements[], counts{}, truncated{}, last_sync_at }
export async function hubspotCompany(params) {
  try {
    const data = await get('company', params)
    return {
      available: true,
      contacts: [],
      deals: [],
      tasks: [],
      engagements: [],
      counts: {},
      truncated: {},
      ...data,
    }
  } catch (err) {
    return failShape(err, {
      company: null,
      contacts: [],
      deals: [],
      tasks: [],
      engagements: [],
      counts: {},
      truncated: {},
    })
  }
}

// Recent-activity digest. Portal-wide, or scoped with `company_id`.
export async function hubspotActivity(params = {}) {
  try {
    return { available: true, activity: [], ...(await get('activity', params)) }
  } catch (err) {
    return failShape(err, { activity: [] })
  }
}

export async function hubspotOwners() {
  try {
    return { available: true, owners: [], ...(await get('owners')) }
  } catch (err) {
    return failShape(err, { owners: [] })
  }
}

// Per-rep results computed server-side over the WHOLE mirror — outreach,
// follow-ups, replies, responses, deals and tasks attributed to one actor —
// with the row lists behind every count (GET /api/hubspot/metrics; see the
// lambda's compute_metrics for the rules, which the response repeats in plain
// English under `definitions`). Same get() as everything else here: a read,
// never a write.
//
// params: { actor, addresses (comma list), owner_ids (comma list),
//           since 'YYYY-MM-DD', until 'YYYY-MM-DD', rows (comma subset),
//           density_bucket 'hour' | 'day' }.
// Arrays are joined for the caller so a page can pass what it holds.
//
// `density_bucket` is the Activity page's hourly/daily toggle asking the server
// for a shape the browser cannot build for itself: hours can be added up into
// days here, but a day or a week can never be split back into the hours it was
// made of, so the choice has to be made where the rows still are. Omitted — or
// '' or 'auto' — means exactly today's behaviour: the lambda picks the bucket
// from the range span on its own.
//
// Resolves { available, reason, ...body }. The empty shapes below are what a
// failure carries so the page can destructure without guarding every field —
// and `complete:false` on a failure means "not computed", which the page must
// show as unavailable, never as zeros.
//
// `density` is the ACTIVITY-DENSITY block the Activity page charts — which
// hours (or days, or weeks) the member's mirrored activity actually landed in.
// It is written by a lambda that deploys on its own schedule, so a mirror one
// deploy behind simply answers without it and the page prints "unavailable —
// not zero" rather than an empty chart. Declared null here so the field is
// part of the shape a page can destructure, exactly like the rest.
//
// That same schedule is why `density_bucket` is a REQUEST and not a promise. A
// mirror one deploy behind has never heard of the param, ignores it, and answers
// with whatever bucket its own range rule chose — so a page that asked for hours
// can be handed days, in a payload that looks perfectly healthy. The page checks
// the `bucket` it got back against the one it asked for and, when they disagree,
// prints "unavailable — not zero" for the view it could not get. Drawing day
// bars under an hourly heading would be a chart answering a question nobody
// asked, and that is worse than a sentence admitting the server did not.
//
// THE CONTRACT the lambda has to meet, because normalizeDensity
// (lib/activityMetrics.js) is written against it and the page's honesty rests
// on it:
//
//   bucket     'hour' | 'hourofday' | 'day' | 'week' — which bucket the answer
//              was actually cut in. With no `density_bucket` the lambda picks it
//              from the range, as it always has (hours for a single day, days
//              for a short range, Monday-start weeks for a long one). The page
//              never re-buckets; it only reads this to learn what it was sent.
//              A request for 'day' is answered 'day' whatever the span, still
//              clipped to the 400-point ceiling. A request for 'hour' splits in
//              two, because "hour" means two different things depending on how
//              much range there is:
//                • over a range spanning exactly ONE day → 'hour', unchanged
//                  from today: 24 real hour buckets, keys 'YYYY-MM-DDTHH', each
//                  carrying a real instant in `start`.
//                • over a range spanning MORE than one day → 'hourofday':
//                  exactly 24 bars, keys '00'..'23', labels '12am'..'11pm',
//                  every activity in the range added to the bar for the `tz`
//                  hour it landed in and summed across every day of the range.
//                  This is the "which hours is this person at the desk" profile,
//                  and it is the only hourly question that still means something
//                  over a month. Their `start` MUST be '' — an hour-of-day bar
//                  stands for no single instant, and inventing one would park
//                  the "still filling" marker on an essentially random bar.
//   span_days  how many days the answered range covers, counted in `tz`. It is
//              what decides 'hour' against 'hourofday' for a request, so the
//              server states it instead of leaving the page to re-derive it from
//              the dates it happened to send and hope the two agree.
//   capped     true when the range asked for more buckets than the 400-point
//              ceiling allows and the lambda clipped to fit — the bars are then
//              PART of the range, not all of it, and the page has to say so.
//              A clipped chart that reads as the whole story is the same lie as
//              a zero nobody computed, just harder to spot.
//   tz         the IANA zone the buckets were cut in, e.g. 'America/Chicago'.
//              REQUIRED for an hour or hour-of-day bucket to mean anything;
//              when it is missing the page says the zone was not stated rather
//              than letting the reader assume Chicago.
//   points[]   one per bucket, in time order: { key, label, start, total,
//              kinds: { <kind key>: count } }. `key` must be UNIQUE (it is the
//              bar's React key). `start` must be epoch ms or an ISO string
//              CARRYING ITS OFFSET ('…Z' or '…±HH:MM') — an offset-less
//              wall-clock string is dropped, because parsing it would resolve
//              it in the viewer's zone and put "still filling" on the wrong
//              hour. The one bucket that sends no `start` at all is
//              'hourofday', which has no instant to send. Send every bucket in
//              the range, including the empty ones and the ones still in the
//              future: the page marks the tail that has not happened, which it
//              cannot do for buckets it never saw.
//   kinds[]    { key, label } in display order — the ONLY place the kinds get
//              named. The page's heading and tooltips are built from these, so
//              a lambda that counts sent email only should label it 'Emails
//              sent', and one that counts tasks or deals has to list them here
//              or they are in no caption.
//   busiest    { key } naming a point it sent, optional (the page falls back
//              to the tallest bar it has).
//   total      the range's count. undatable: rows that carried no timestamp
//              and are therefore in no bar — the reader's only way to know the
//              bars are the whole story.
const METRICS_EMPTY = {
  complete: false,
  totals: null,
  all_time: null,
  days: [],
  rows: {},
  rows_truncated: {},
  excluded: {},
  scanned: {},
  senders: [],
  actor: null,
  ours: null,
  density: null,
  definitions: {},
}

export async function hubspotMetrics(params = {}) {
  const query = {}
  for (const [key, value] of Object.entries(params)) {
    query[key] = Array.isArray(value) ? value.filter(Boolean).join(',') : value
  }
  try {
    const data = await get('metrics', query)
    return { available: true, reason: '', ...METRICS_EMPTY, ...data }
  } catch (err) {
    const shape = failShape(err, { ...METRICS_EMPTY })
    // Keep the HTTP status when the server answered, so the page can tell
    // "sign in again" (401) from "not a member" (403) from "unreachable".
    if (err instanceof HubSpotHttpError) shape.status = err.status
    return shape
  }
}

// ── the daily report ─────────────────────────────────────────────────────────
// POST /api/hubspot/daily-report — today's seven Activity tiles as a one-page
// PDF, emailed by the mirror lambda to the address it is configured with, with
// yesterday and four per-weekday averages beside each number. Body: the same
// identity the page sends /metrics (actor, addresses, owner_ids) plus the
// member's display name for the header. Resolves { available, ok, emailed,
// reason, to, subject, day, day_label, as_of, report, … }; never throws.
// `emailed:false` with a reason is a real answer (SES refused, mirror unread),
// not a transport failure — the page shows the reason either way.
const REPORT_EMPTY = {
  ok: false,
  emailed: false,
  reason: '',
  to: [],
  subject: '',
  day: '',
  day_label: '',
  as_of: '',
  synced: '',
  report: null,
}

export async function hubspotDailyReport(body = {}) {
  try {
    const data = await post('daily-report', body)
    return { available: true, ...REPORT_EMPTY, ...data }
  } catch (err) {
    const shape = failShape(err, { ...REPORT_EMPTY })
    if (err instanceof HubSpotHttpError) shape.status = err.status
    if (!shape.reason) shape.reason = 'The daily report could not be sent: the HubSpot mirror could not be reached from this browser.'
    return shape
  }
}

// ── one call, whole ──────────────────────────────────────────────────────────
// GET /api/hubspot/call?id= — the record behind one row of the Activity page's
// calls ledger: the rep's notes, HubSpot's AI summary (as sections of text),
// the outcome and whether anyone picked up, the numbers dialled from and to,
// which of HubSpot's own records exist (transcript, recording, voicemail),
// the company / contact / owner it hangs off, and the review page URL.
// Resolves { available, found, deleted, call, reason, definitions,
// last_sync_at }; never throws, like every read in this module. `found:false`
// with `available:true` is a real answer — the mirror was asked and holds no
// such call (yet: a call placed minutes ago lands on the next sync).
const CALL_EMPTY = { found: false, deleted: false, call: null, reason: '', definitions: {}, last_sync_at: '' }

export async function hubspotCall(id, { signal } = {}) {
  const callId = String(id ?? '').trim()
  if (!/^\d+$/.test(callId)) return { available: false, ...CALL_EMPTY, reason: 'Not a HubSpot call id.' }
  try {
    const data = await get('call', { id: callId }, { signal })
    return { available: true, reason: '', ...CALL_EMPTY, ...data }
  } catch (err) {
    const shape = failShape(err, { ...CALL_EMPTY })
    if (err instanceof HubSpotHttpError) shape.status = err.status
    return shape
  }
}

// ── product usage: the portal's custom contact properties, aggregated ────────
// GET /api/hubspot/users — every mirrored contact carrying at least one of the
// portal's custom_* product properties (last login, last software access, last
// analysis runs, subscription/trial ends, project & blueprint counts, …), plus
// `by_company`, the same facts rolled up per company id (datetimes → newest,
// counts → summed; the lambda's `definitions` restate the rules in plain
// English). The LEADS TABLE asks for this once per mount and joins every row
// against `by_company` locally, through the match the data layer already made
// (row._hubspot.companyId) — one call for the whole screen, never one per row.
//
// Resolves { available, reason, ...body } like hubspotMetrics. The empty shape
// is what a failure carries so a page can destructure without guarding — and
// `complete:false` on a failure means "not computed": the columns must render
// dashes and say the data could not be read, never zeros. An aborted request
// (the page unmounted mid-read) resolves the same unreachable shape; the caller
// that aborted is gone, so nobody renders it.
const USERS_EMPTY = {
  complete: false,
  users: [],
  by_company: {},
  scanned: {},
  definitions: {},
  last_sync_at: '',
  computed_at: '',
}

export async function hubspotUsers({ signal } = {}) {
  try {
    const data = await get('users', {}, { signal })
    return { available: true, reason: '', ...USERS_EMPTY, ...data }
  } catch (err) {
    const shape = failShape(err, { ...USERS_EMPTY })
    // Keep the HTTP status when the server answered, so the page can tell
    // "sign in again" (401) from "not a member" (403) from "unreachable" —
    // same convention as hubspotMetrics.
    if (err instanceof HubSpotHttpError) shape.status = err.status
    return shape
  }
}

// ── the match index (one call matches every visible row locally) ─────────────
// GET /api/hubspot/index returns a compact map of every mirrored company keyed
// by website-domain / contact-email / name-slug, plus a companies{} lookup of
// bounded identifiers (name, owner, domain, lifecycle, rollup). The DIRECTORY
// asks for it ONCE per session and matches every row on screen against it in
// memory — never a fetch per row, never one call per region. It is cached at
// module scope so a second region, a remount, or a route change reuses the
// first fetch.
//
// It fails SOFT to null. Unlike the honest-shape endpoints above it does not
// return an {available:false} envelope: annotation is best-effort decoration on
// a directory that must render fully with or without a mirror, so every failure
// mode — unreachable, never-configured, forbidden — collapses to the same "no
// index, annotate nothing". A resolved-null is NOT cached (a later navigation
// may find the mirror configured or reachable); only a real, configured index
// is cached for the rest of the session.
let _indexPromise = null
export function hubspotIndex() {
  if (!_indexPromise) {
    _indexPromise = get('index')
      .then((data) => {
        const ok = data && typeof data === 'object' && data.configured ? data : null
        if (!ok) _indexPromise = null // don't poison the session on a soft miss
        return ok
      })
      .catch(() => {
        // 403 (not a member) and transport failures alike: the directory does
        // not get to annotate, but it does not break either. Allow a retry.
        _indexPromise = null
        return null
      })
  }
  return _indexPromise
}

// Test seam: drop the cached index so a fresh session (or a harness) re-fetches.
export function _resetHubspotIndexCache() {
  _indexPromise = null
}

// ── matching a masonry lead row to a HubSpot company ─────────────────────────

// Normalize a website/URL to a bare host the way the mirror stores its
// `alias#domain#<d>` keys: strip scheme, path and query, drop a leading www.,
// lowercase. Exported because the client-side index match (data.jsx) MUST key
// rows exactly the way the backend keyed the aliases, or a domain that is in
// by_domain would silently miss.
export function normDomain(value) {
  let v = String(value || '').trim().toLowerCase()
  if (!v) return ''
  // Backend _norm_domain uses `split("://", 1)[1]` — everything after the FIRST
  // "://". JS split(sep, 1) truncates the RESULT to one element instead, so use
  // indexOf to keep the two byte-identical on double-scheme URLs. The trailing
  // slice(0, 200) matches the backend's cap; without it a pathological host
  // would key differently on each side and silently miss.
  const i = v.indexOf('://')
  if (i !== -1) v = v.slice(i + 3)
  v = v.split('/')[0].split('?')[0].trim()
  if (v.startsWith('www.')) v = v.slice(4)
  return v.slice(0, 200)
}

// Normalize a company name to the mirror's `alias#name#<slug>` key: lowercase,
// strip accents, collapse every run of non-alphanumerics to a single space,
// trim. Name is the WEAKEST match tier (a shared surname is a coin-flip in this
// trade, and the index reports those as "__ambiguous__"), so a slug that drifts
// from the backend's only ever costs a marginal name-only hit and degrades
// safely to 'import' — it can never manufacture a false match.
export function nameSlug(value) {
  // Match the backend _slug EXACTLY: it lowercases and keeps every Unicode
  // alphanumeric (Python str.isalnum), replacing only non-alnum with a space.
  // It does NOT strip accents \u2014 so "Caf\u00e9" keys as "caf\u00e9" on both sides. The
  // earlier NFKD accent-strip made "Caf\u00e9 Masonry" miss its own alias. `\p{L}`+
  // `\p{N}` is the JS equivalent of isalnum; cap at 120 like the backend.
  return String(value || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .slice(0, 120)
}

// The index's own word for "this key names more than one company".
export const HS_AMBIGUOUS = '__ambiguous__'

// Consumer mail hosts: an address there says nothing about which company a
// person works for, so its domain never names a company record. Byte-for-byte
// the backend's _SHARED_MAIL_HOSTS (hubspot/handler.py) — the two matchers
// must narrow the same way or the card and the server disagree about a lead.
export const SHARED_MAIL_HOSTS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'comcast.net', 'msn.com', 'live.com',
])

/**
 * THE COMPANY A LEAD'S EMAIL DOMAIN NAMES — an exact key on the company
 * record's stored domain, read off the index. '' when the host is shared,
 * absent, unknown to the index, or itself ambiguous.
 *
 * WHY IT EXISTS (Ball Construction, 2026-09-11). The row carried no website,
 * its contact mike@ballcc.com was associated with TWO company records in
 * HubSpot (the auto-created "Ballcc", domain ballcc.com, holding every logged
 * email — and a second company he had been cc'd into), so the contact tier
 * was ambiguous and the matcher fell through to the NAME tier, which hit the
 * import's own empty "Ball Construction" record. The card linked there, and
 * "View thread" on a lead Bo had emailed four times read "Nothing on this
 * company yet". The address's own domain settles that ambiguity: it is the
 * one exact key the two records do not share.
 */
export function emailDomainCompanyId(email, index) {
  const addr = String(email || '').trim().toLowerCase()
  const at = addr.lastIndexOf('@')
  if (at === -1 || !index || !index.by_domain || !index.companies) return ''
  const host = normDomain(addr.slice(at + 1))
  if (!host || SHARED_MAIL_HOSTS.has(host)) return ''
  if (!Object.prototype.hasOwnProperty.call(index.by_domain, host)) return ''
  const value = index.by_domain[host]
  if (value === HS_AMBIGUOUS) return ''
  const id = String(value || '')
  return id && index.companies[id] ? id : ''
}

function tierHit(key, map) {
  if (!key || !map || !Object.prototype.hasOwnProperty.call(map, key)) return { id: '' }
  const value = map[key]
  if (value === HS_AMBIGUOUS) return { id: '', ambiguous: true }
  return { id: String(value || '') }
}

/**
 * Resolve one lead stub (a receipt card's {name, email, website?}) to a
 * mirrored company id through the index — the chat cards' matcher, in the
 * precedence the directory uses (data.jsx#resolveRow): website domain, then
 * contact email, then the EMAIL'S OWN DOMAIN, then company-name slug. The
 * first tier the index RECOGNISES decides — hit or ambiguous — except that an
 * ambiguous tier lets the next, independent signal have its say (a contact
 * attached to two CRM companies says nothing about which record this card
 * is). A key that points at a company the index does not carry is a
 * half-answer and ends the search with '' rather than linking at a guess.
 *
 * The email-domain tier sits BETWEEN email and name on purpose: it is an exact
 * key on the company record (the same evidence the backend's email_domain
 * tier uses), where a name slug is a coin-flip in a trade full of surnames —
 * and it is what keeps an ambiguous contact from falling through to the wrong
 * namesake (see emailDomainCompanyId).
 */
export function companyIdFor(lead, index) {
  if (!lead || !index || !index.companies) return ''
  const email = String(lead.email || '').trim().toLowerCase()
  const tiers = [
    () => tierHit(normDomain(lead.website || lead.domain || ''), index.by_domain),
    () => tierHit(email, index.by_email),
    () => ({ id: emailDomainCompanyId(email, index) }),
    () => tierHit(nameSlug(lead.name || ''), index.by_name),
  ]
  for (const tier of tiers) {
    const { id, ambiguous } = tier()
    if (ambiguous) continue
    if (id) return index.companies[id] ? id : ''
  }
  return ''
}

// What we hand the matcher, from a masonry lead row (snake_case client-side).
// `extraEmail` lets the caller add a contact email the row itself doesn't carry
// (the packed `contacts` blob, or an enrichment result).
export function leadLookup(company, extraEmail = '') {
  if (!company) return { lookup: {}, tried: [] }
  const website = String(company.website || '').trim()
  const domain = normDomain(website)
  const name = String(company.name || '').trim()
  const email = String(company.email || extraEmail || '').trim()

  const lookup = {}
  if (domain) lookup.domain = domain
  if (website) lookup.website = website
  if (name) lookup.name = name
  if (email) lookup.email = email

  // The same facts, labelled for display: when a lead does NOT match, a rep is
  // owed the list of what we searched on, so they can see whether the miss is
  // "not in the CRM" or "our row has no website".
  const tried = [
    { label: 'Website domain', value: domain },
    { label: 'Company name', value: name },
    { label: 'Email', value: email },
  ]
  return { lookup, tried }
}

// How the server found it, in words a rep can judge.
const STRONG_MATCH = {
  id: 'a direct HubSpot record link',
  domain: 'an exact website-domain match',
  email: 'an exact contact-email match',
}
const WEAK_MATCH = {
  email_domain: "the lead email's domain",
  name: 'the company name alone',
}

// Normalize the server's candidate list. It sends objects
// ({id, name, domain, hubspot_url}); older payloads sent bare id strings, and a
// build that outlives its lambda must not crash rendering one. Anything
// unrecognisable is dropped rather than rendered as "[object Object]".
export function candidateList(match) {
  const raw = Array.isArray(match && match.candidates) ? match.candidates : []
  return raw
    .map((c) => {
      if (typeof c === 'string') return { id: c, name: '', domain: '', url: '' }
      if (c && typeof c === 'object') {
        return {
          id: String(c.id || ''),
          name: String(c.name || ''),
          domain: String(c.domain || ''),
          url: String(c.hubspot_url || ''),
        }
      }
      return null
    })
    .filter((c) => c && c.id)
}

// The whole point of this function: an ambiguous hit is NOT a match.
//
// The mirror's matcher is exact-key only, but "exact" is not the same as
// "unique" — two HubSpot companies can carry the same domain, one contact
// email can be associated with both a holding company and its operating
// entity, and a name-slug hit on "Kelly Masonry" is a genuine coin-flip in a
// trade where half the portal shares a surname. Every one of those renders as
// candidates the rep settles in HubSpot, never as this lead's CRM record.
//
// AMBIGUITY IS CHECKED FIRST, BEFORE `matched`. The server already refuses to
// guess (it answers matched:false + ambiguous:true), so the old ordering —
// `if (!matched) return none` above the candidate count — would have swallowed
// that refusal into a bare "not in HubSpot" and thrown away the candidate list
// the rep needs. Checking ambiguity first means the two layers agree instead of
// cancelling out, and an older lambda that still says matched:true with several
// candidates is still caught here.
//
// Returns one of:
//   unavailable | unconfigured | none | ambiguous | unconfirmed | matched
export function matchVerdict(match) {
  if (!match || !match.available) {
    return { state: 'unavailable', confident: false, reason: (match && match.reason) || '' }
  }
  if (!match.configured) {
    return { state: 'unconfigured', confident: false, reason: match.reason || '' }
  }
  const confidence = String(match.confidence || '')
  const candidates = candidateList(match)
  // candidate_count is the true total; `candidates` is capped for display, so
  // trusting only the array length would under-report a 12-way tie as 10.
  const count = Math.max(Number(match.candidate_count) || 0, candidates.length)
  if (match.ambiguous || count > 1) {
    return {
      state: 'ambiguous',
      confident: false,
      confidence,
      count,
      candidates,
      basis: STRONG_MATCH[confidence] || WEAK_MATCH[confidence] || 'the details on this lead',
    }
  }
  if (!match.matched || !match.company_id) {
    return { state: 'none', confident: false, candidates: [], reason: match.reason || '' }
  }
  if (WEAK_MATCH[confidence]) {
    return {
      state: 'unconfirmed',
      confident: false,
      confidence,
      candidates,
      basis: WEAK_MATCH[confidence],
    }
  }
  return {
    state: 'matched',
    confident: true,
    confidence,
    candidates,
    basis: STRONG_MATCH[confidence] || 'an exact match',
  }
}

// ── value formatting (mirror strings -> something a rep can read) ────────────
// Mirror props are stored as HubSpot sent them: mostly ISO-8601, occasionally
// epoch milliseconds. Both parse here; anything else returns null and the
// caller shows a dash rather than "Invalid Date".
export function hubspotDate(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  if (/^\d{10,}$/.test(raw)) {
    const d = new Date(Number(raw))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

export function formatDay(value) {
  const d = hubspotDate(value)
  if (!d) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatDayTime(value) {
  const d = hubspotDate(value)
  if (!d) return ''
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatAmount(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const n = Number(raw)
  if (!Number.isFinite(n)) return raw
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  })
}

export function formatCount(value) {
  // An empty property must stay empty. Number('') is 0, so without this guard a
  // company with no headcount on file would render a confident "0 employees".
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const n = Number(raw)
  if (!Number.isFinite(n)) return raw
  return n.toLocaleString()
}

// HubSpot internal enum values (`appointmentscheduled`, `SALESQUALIFIEDLEAD`,
// `NOT_STARTED`) made readable. Custom pipeline stages are numeric ids the
// mirror doesn't resolve to labels — those pass through untouched rather than
// being dressed up as something they aren't.
export function humanizeEnum(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if (/^\d+$/.test(raw)) return raw
  const spaced = raw.replace(/[_-]+/g, ' ').trim()
  if (spaced.includes(' ')) {
    return spaced.replace(/\b\w/g, (c) => c.toUpperCase())
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

// ── open / closed ────────────────────────────────────────────────────────────
// The mirror carries HubSpot's own `hs_is_closed` for deals, so this is the
// portal's answer and not our guess.
export function isOpenDeal(deal) {
  return !!deal && !deal.is_closed
}

const DONE_TASK_STATUS = new Set(['completed', 'complete', 'done', 'deferred'])

export function isOpenTask(task) {
  if (!task) return false
  if (task.completed_at) return false
  return !DONE_TASK_STATUS.has(String(task.status || '').trim().toLowerCase())
}

// An overdue open task is the one thing on this panel a rep should act on
// today, so it gets called out.
export function isOverdue(task) {
  const due = hubspotDate(task && task.due_at)
  return !!due && due.getTime() < Date.now()
}

// ── freshness ────────────────────────────────────────────────────────────────
// A mirror that is quietly hours behind is worse than no mirror, because it
// looks live. `syncAge` is what the lead page prints next to the section title.
// `stale` at 30 minutes is deliberately tighter than the sync cadence needs to
// be: it should nag slightly early rather than reassure late.
export function syncAge(iso, now = Date.now()) {
  const d = hubspotDate(iso)
  if (!d) return { known: false, label: 'sync time unknown', stale: true, minutes: null }
  const minutes = Math.max(0, Math.round((now - d.getTime()) / 60000))
  let label
  if (minutes < 1) label = 'synced just now'
  else if (minutes < 60) label = `synced ${minutes}m ago`
  else if (minutes < 60 * 24) label = `synced ${Math.floor(minutes / 60)}h ago`
  else label = `synced ${Math.floor(minutes / 1440)}d ago`
  return { known: true, label, minutes, stale: minutes >= 30, at: d }
}

// ── email engagements: the mirror IS the send history ───────────────────────
//
// ADDITIVE, READ-ONLY. Everything below goes through the same get() as the rest
// of this file, so it is a GET or it does not happen.
//
// WHY IT LIVES HERE. Bo sends from HubSpot, not from this app, so the only true
// record of "who did we write to, and when did they answer" is the mirror's
// EMAIL engagements. lib/mailHistory.js turns them into threads; this section
// is the wire format — one place that knows HubSpot's property names, so a
// renamed prop is a one-line fix instead of a hunt through the engine.

// HubSpot's own direction values on an email engagement.
export const ENGAGEMENT_OUTBOUND = 'EMAIL'
export const ENGAGEMENT_INBOUND = 'INCOMING_EMAIL'

// hs_email_status values that mean the message did NOT reach a human. A bounce
// is not a touch and a draft is not a send: counting either would start the
// cadence's wait gate on a message nobody received.
const UNDELIVERED_EMAIL_STATUS = new Set([
  'BOUNCED', 'FAILED', 'DRAFT', 'SCHEDULED', 'SENDING', 'ERROR',
])

/**
 * Was this email actually delivered? An EMPTY status is treated as delivered:
 * the mirror carries no status on plenty of older rows, and refusing to count
 * those would erase most of the real history. Only an explicit failure state
 * takes a message out of the touch count.
 */
export function isDeliveredEmailStatus(status) {
  const s = String(status ?? '').trim().toUpperCase()
  if (!s) return true
  return !UNDELIVERED_EMAIL_STATUS.has(s)
}

// The first non-empty value among several possible property names. The mirror's
// _shape() flattens hs_email_* into short keys, but older payloads (and the
// per-company `engagements[]` array) carry the raw HubSpot props — reading both
// means a shape change degrades to a missing field, never to a crash.
function pick(row, ...names) {
  for (const n of names) {
    const v = row?.[n]
    if (v === undefined || v === null) continue
    const s = String(v).trim()
    if (s) return s
  }
  return ''
}

function idList(row, ...names) {
  const out = []
  for (const n of names) {
    const v = row?.[n]
    if (v === undefined || v === null) continue
    const items = Array.isArray(v) ? v : String(v).split(/[,\s]+/)
    for (const item of items) {
      const s = String(item ?? '').trim()
      if (s && !out.includes(s)) out.push(s)
    }
  }
  return out
}

/**
 * One mirrored engagement in the shape lib/mailHistory.js reads. Pure — no
 * network, no throw. An unrecognisable row still comes back with empty fields
 * rather than undefined, so callers never guard for shape.
 *
 * @returns {{id, kind, direction, from, to, subject, body, at, status, ownerId,
 *   companyIds: string[], url}}
 */
export function normalizeEngagement(raw) {
  const row = raw && typeof raw === 'object' ? raw : {}
  return {
    id: pick(row, 'id', 'engagement_id', 'object_id'),
    kind: pick(row, 'kind', 'type', 'engagement_type').toUpperCase(),
    direction: pick(row, 'direction', 'email_direction', 'hs_email_direction').toUpperCase(),
    from: pick(row, 'from', 'from_email', 'hs_email_from_email', 'from_address'),
    to: pick(row, 'to', 'to_email', 'hs_email_to_email', 'to_address', 'recipients'),
    subject: pick(row, 'subject', 'title', 'hs_email_subject'),
    // MESSAGE TEXT ONLY. `summary` used to close this chain, and it is a
    // SERVER-GENERATED description ("Bo sent an email to …"), not anything the
    // sender wrote. Every other chain here degrades to an EMPTY field; this one
    // degraded to a sentence that the card then renders inside curly quotes as
    // the email's own words, cited to HubSpot. An absent body is an absent
    // body.
    body: pick(row, 'body', 'text', 'hs_email_text'),
    at: pick(row, 'timestamp', 'occurred_at', 'hs_timestamp', 'updated_at', 'created_at'),
    status: pick(row, 'status', 'email_status', 'hs_email_status'),
    ownerId: pick(row, 'owner_id', 'hubspot_owner_id'),
    companyIds: idList(row, 'company_ids', 'companyIds', 'CompanyIds', 'company_id'),
    url: pick(row, 'hubspot_url', 'url'),
  }
}

// The mirror is newest-first and the read API caps a page at 200.
export const ENGAGEMENT_PAGE_MAX = 200

/**
 * One page of EMAIL engagements, normalized.
 *
 * Fails soft exactly like every other function in this module: an unreachable
 * or forbidden mirror resolves to { available:false, reason, engagements: [] }.
 * `cursor` is echoed back only when the server actually sent one — a caller
 * that gets '' must not pretend it read the whole history.
 *
 * @param {{limit?:number, since?:string, cursor?:string, company_id?:string}} params
 */
export async function hubspotEngagementPage(params = {}) {
  const limit = Math.min(
    ENGAGEMENT_PAGE_MAX,
    Math.max(1, Number(params.limit) || ENGAGEMENT_PAGE_MAX),
  )
  try {
    const data = await get('activity', {
      family: 'engagement',
      limit,
      ...(params.since ? { since: params.since } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.company_id ? { company_id: params.company_id } : {}),
    })
    const rows = Array.isArray(data?.activity)
      ? data.activity
      : Array.isArray(data?.engagements)
        ? data.engagements
        : []
    return {
      available: true,
      reason: '',
      engagements: rows.map(normalizeEngagement),
      cursor: String(data?.cursor || data?.next_cursor || data?.next || ''),
      configured: data?.configured !== false,
      last_sync_at: data?.last_sync_at || '',
    }
  } catch (err) {
    const shape = failShape(err, { engagements: [], cursor: '' })
    return { ...shape, engagements: [], cursor: '' }
  }
}
